<template>
  <div class="min-w-0 space-y-6">
    <div class="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <div class="text-sm font-semibold text-slate-700">Overview</div>
        <h2 class="mt-1 text-2xl font-bold tracking-tight text-slate-900">Dashboard</h2>
      </div>
      <div class="rounded-lg bg-white px-3 py-2 text-xs text-slate-600 ring-1 ring-slate-200">
        Guest pricing:
        <span class="font-semibold text-slate-900">{{ pricing.firstHourRate }}</span> ETB first hour,
        <span class="font-semibold text-slate-900">{{ pricing.extraHourRate }}</span> ETB / extra hour
      </div>
    </div>

    <div class="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <div class="min-w-0 rounded-2xl bg-white p-3 ring-1 ring-slate-200 sm:p-4">
        <div class="text-[11px] font-semibold leading-tight text-slate-600 sm:text-xs">Active inside</div>
        <div class="mt-2 text-xl font-bold tabular-nums text-slate-900 sm:text-2xl lg:text-3xl">{{ activeCount }}</div>
      </div>
      <div class="min-w-0 rounded-2xl bg-white p-3 ring-1 ring-slate-200 sm:p-4">
        <div class="text-[11px] font-semibold leading-tight text-slate-600 sm:text-xs">Today revenue</div>
        <div class="mt-2 flex flex-wrap items-baseline gap-x-1 text-xl font-bold tabular-nums text-slate-900 sm:text-2xl lg:text-3xl">
          <span>{{ fmt(todayRevenue) }}</span>
          <span class="text-sm font-semibold text-slate-700 sm:text-base lg:text-lg">ETB</span>
        </div>
      </div>
      <div class="min-w-0 rounded-2xl bg-white p-3 ring-1 ring-slate-200 sm:p-4">
        <div class="text-[11px] font-semibold leading-tight text-slate-600 sm:text-xs">This month revenue</div>
        <div class="mt-2 flex flex-wrap items-baseline gap-x-1 text-xl font-bold tabular-nums text-slate-900 sm:text-2xl lg:text-3xl">
          <span>{{ fmt(monthRevenue) }}</span>
          <span class="text-sm font-semibold text-slate-700 sm:text-base lg:text-lg">ETB</span>
        </div>
      </div>
      <div class="min-w-0 rounded-2xl bg-white p-3 ring-1 ring-slate-200 sm:p-4">
        <div class="text-[11px] font-semibold leading-tight text-slate-600 sm:text-xs">Expired subscriptions</div>
        <div class="mt-2 text-xl font-bold tabular-nums text-rose-700 sm:text-2xl lg:text-3xl">{{ expiredCount }}</div>
      </div>
    </div>

    <div class="grid min-w-0 gap-6 lg:grid-cols-2">
      <section class="min-w-0 max-w-full rounded-2xl bg-white p-5 ring-1 ring-slate-200">
        <div class="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
          <div class="min-w-0">
            <div class="text-xs font-semibold text-slate-600">Vehicles currently inside</div>
            <div class="mt-1 text-sm font-bold text-slate-900">Active sessions</div>
          </div>
          <RouterLink
            to="/admin/reports"
            class="shrink-0 text-xs font-semibold text-sky-700 hover:text-sky-600"
          >
            View reports →
          </RouterLink>
        </div>
        <div
          class="mt-4 max-w-full overflow-x-auto overscroll-x-contain rounded-xl ring-1 ring-slate-200 [-webkit-overflow-scrolling:touch]"
        >
          <table class="w-full min-w-[560px] text-left text-xs">
            <thead class="bg-slate-50 text-slate-600">
              <tr>
                <th class="px-3 py-2 font-semibold">Plate</th>
                <th class="px-3 py-2 font-semibold">Type</th>
                <th class="px-3 py-2 font-semibold">Entry time</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="s in activeSessions.slice(0, 8)" :key="s.id" class="border-t border-slate-200">
                <td class="px-3 py-2 font-semibold text-slate-900">{{ s.plateNumber }}</td>
                <td class="px-3 py-2">
                  <span
                    class="rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1"
                    :class="s.type === 'permanent' ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' : 'bg-sky-50 text-sky-700 ring-sky-200'"
                  >
                    {{ s.type }}
                  </span>
                </td>
                <td class="px-3 py-2 text-slate-600">{{ dt(s.entryTime) }}</td>
              </tr>
              <tr v-if="activeSessions.length === 0">
                <td class="px-3 py-6 text-center text-slate-500" colspan="3">No active vehicles right now.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="min-w-0 max-w-full rounded-2xl bg-white p-5 ring-1 ring-slate-200">
        <div class="text-xs font-semibold text-slate-600">Quick stats</div>
        <div class="mt-1 text-sm font-bold text-slate-900">Guest vs Permanent</div>
        <div class="mt-4 grid grid-cols-2 gap-3">
          <div class="min-w-0 rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200">
            <div class="text-xs font-semibold text-slate-600">Active guests</div>
            <div class="mt-2 text-2xl font-bold tabular-nums">{{ activeGuests }}</div>
          </div>
          <div class="min-w-0 rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200">
            <div class="text-xs font-semibold text-slate-600">Active permanents</div>
            <div class="mt-2 text-2xl font-bold tabular-nums">{{ activePermanents }}</div>
          </div>
          <div class="col-span-2 min-w-0 rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200">
            <div class="text-xs font-semibold text-slate-600">Total completed sessions</div>
            <div class="mt-2 text-2xl font-bold tabular-nums">{{ completedCount }}</div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useStore } from "vuex";

const store = useStore();

const pricing = computed(() => store.state.settings.guestPricing);
const activeSessions = computed(() => store.getters.activeSessions);
const activeCount = computed(() => activeSessions.value.length);
const completedCount = computed(() => store.getters.completedSessions.length);

const todayRevenue = computed(() => store.getters.todayRevenue);
const monthRevenue = computed(() => store.getters.monthRevenue);
const expiredCount = computed(() => store.getters.expiredPermanentCustomers.length);

const activeGuests = computed(() => activeSessions.value.filter((x) => x.type === "guest").length);
const activePermanents = computed(() => activeSessions.value.filter((x) => x.type === "permanent").length);

function fmt(v) {
  const n = Number(v) || 0;
  return n.toFixed(2);
}

function dt(iso) {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
</script>

